import { useState } from 'react';
import PropTypes from 'prop-types';

import { useMediaQuery, useClickOutside } from '@mantine/hooks';
import { breakpoints } from 'styles/theme.variables';
import { Box, Group, NumberInput, Text, Tooltip } from '@mantine/core';

import { KEY_NAMES } from 'constants/global';
import { FIELD_KEYS } from './constants';
import { validateField } from './helper';
import styles from './EditableMinMax.module.scss';

import { CloseIconSm } from '@chownow/pantry/icons';
export default function EditableMinMax({ disableEdit, handleEdit, modifierGroup }) {
  const [editingField, setEditingField] = useState(null);
  const [currentValue, setCurrentValue] = useState('');
  const [error, setError] = useState(null);

  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.lg})`);
  const { minimum, maximum } = modifierGroup;

  const clearEditingState = () => {
    setEditingField(null);
    setError(null);
  };

  const handleSave = (field, forceClose = false) => {
    // If there's already an error and we're forcing close, just clear everything
    if (forceClose && error) {
      clearEditingState();
      return;
    }

    // Early return for empty input
    if (!currentValue && currentValue !== 0) {
      clearEditingState();
      return;
    }

    // Validate the new value
    const validationError = validateField(field, currentValue, modifierGroup);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Save if value changed
    const originalValue = field === FIELD_KEYS.min ? minimum : maximum;
    if (currentValue !== originalValue) {
      handleEdit(field, currentValue);
    }

    clearEditingState();
  };

  const handleKeyDown = (event, field) => {
    if (event.key === KEY_NAMES.enter) {
      event.preventDefault();
      handleSave(field);
    } else if (event.key === KEY_NAMES.escape) {
      event.preventDefault();
      clearEditingState();
    }
  };

  const handleClear = () => {
    setCurrentValue('');
  };

  const startEditing = (field, value) => {
    if (disableEdit) return;

    if (isDesktop) {
      setCurrentValue(value);
      setEditingField(field);
      setError(null);
    }
  };

  const renderField = (field, value, label) => {
    const isEditing = editingField === field;

    // Use click outside hook to save when clicking away
    const ref = useClickOutside(() => handleSave(field, true));

    if (isEditing) {
      return (
        <div ref={ref} className={styles.inputWrapper}>
          <Tooltip
            label={error}
            opened={!!error}
            position="top"
            withArrow
            color="red"
            multiline
            w={220}
          >
            <NumberInput
              value={currentValue}
              onChange={setCurrentValue}
              onKeyDown={e => handleKeyDown(e, field)}
              rightSectionPointerEvents="all"
              rightSection={
                <CloseIconSm
                  aria-label="Clear input"
                  onClick={handleClear}
                  className={
                    currentValue || currentValue === 0 ? styles.clearIcon : styles.clearIconHidden
                  }
                />
              }
              size="sm"
              autoFocus
              allowNegative={false}
              hideControls
              error={!!error}
              mb={0}
              className={styles.numberInput}
            />
          </Tooltip>
        </div>
      );
    }

    return (
      <Text
        component="span"
        onClick={() => startEditing(field, value)}
        style={{
          cursor: disableEdit ? 'not-allowed' : 'pointer',
        }}
      >
        {label} {value}
      </Text>
    );
  };

  return (
    <Box className={styles.container}>
      <Group gap="xs" justify="flex-start" wrap="nowrap">
        <Text variant="sublabel-sm" component="span">
          {renderField(FIELD_KEYS.min, minimum, 'Min')}
        </Text>
        <Text variant="sublabel-sm" component="span">
          {renderField(FIELD_KEYS.max, maximum, 'Max')}
        </Text>
      </Group>
    </Box>
  );
}

EditableMinMax.propTypes = {
  disableEdit: PropTypes.bool,
  handleEdit: PropTypes.func.isRequired,
  modifierGroup: PropTypes.shape({
    minimum: PropTypes.number,
    maximum: PropTypes.number,
    children: PropTypes.arrayOf(PropTypes.shape({})),
    individualMinimum: PropTypes.number,
    individualMaximum: PropTypes.number,
  }).isRequired,
};
